import React, { useEffect, useState } from 'react'
import { AxiosError } from 'axios'
import {
  AdminsApi,
  AdminsEcommerceInventoryStockLocationCreateRequest,
  AdminsEcommerceInventoryStockLocationListResponseItem,
  AdminsEcommerceInventoryStockLocationUpdateRequest,
  AdminsFinanceGetVendorListResponseItem,
  Configuration,
  LocationApi,
  ProvinceResponseItem,
  StockLocationStatusEnum,
  VendorStatusEnum,
} from 'generated/api'
import { axiosDefaultConfig } from 'constants/axios'
import Toast from 'components/toast/Toast'
import { enumToastMessage } from 'constants/toast'
import debounce from 'helpers/debounce'

const axiosConfig = new Configuration(axiosDefaultConfig)
const adminsApi = new AdminsApi(axiosConfig)
const locationApi = new LocationApi(axiosConfig)

export enum StateFormEnum {
  CREATE = 'CREATE',
  UPDATE = 'UPDATE',
}

export const useStockLocation = () => {
  const { toast, toastContainer } = Toast()
  const [isInitFetchStockLocations, setIsInitFetchStockLocations] = useState(true)
  const [isLoadingStockLocations, setIsLoadingStockLocations] = useState(false)
  const [isFailedStockLocations, setIsFailedStockLocations] = useState(false)
  const [statusStockLocations, setStatusStockLocations] = useState('')
  const [stockLocations, setStockLocations] = useState<
    AdminsEcommerceInventoryStockLocationListResponseItem[]
  >([])
  const [totalRowsStockLocations, setTotalRowsStockLocations] = useState(0)
  const [currentPageStockLocations, setCurrentPageStockLocations] = useState(1)
  const [currentLimitStockLocations, setCurrentLimitStockLocations] = useState(10)

  const [isOpenModalFormStockLocation, setIsOpenModalFormStockLocation] = useState(false)
  const [isLoadingFormStockLocation, setIsLoadingFormStockLocation] = useState(false)
  const [initialFormStockLocation, setInitialFormStockLocation] =
    useState<AdminsEcommerceInventoryStockLocationCreateRequest>({
      name: '',
      ownerName: '',
      ownerPhoneNo: '',
      address: '',
      province: '',
      city: '',
      invoiceCode: '',
      latitude: 0,
      longitude: 0,
      openHour: '',
      closeHour: '',
      isShownMember: true,
      isShownWorker: true,
      status: 'ACTIVE',
      vendorId: '',
      referenceLocation: '',
      imageUrlItems: [],
    })
  const [provincesDropdown, setProvincesDropdown] = useState<ProvinceResponseItem[]>([])
  const [stateFormStockLocation, setStateFormStockLocation] = useState<StateFormEnum>(
    StateFormEnum.CREATE,
  )
  const [stockLocationId, setStockLocationId] = useState('')
  const [vendorInitialName, setVendorInitialName] = useState('')
  const [isInitFetchVendors, setIsInitFetchVendors] = useState(true)
  const [isLoadingFetchVendors, setIsLoadingFetchVendors] = useState(false)
  const [isSearchVendor, setIsSearchVendor] = useState(false)
  const [vendorsDropdown, setVendorsDropdown] = useState<AdminsFinanceGetVendorListResponseItem[]>(
    [],
  )
  const [vendorDropdownPage, setVendorDropdownPage] = useState(1)
  const [vendorsDropdownPages, setVendorsDropdownPages] = useState(1)
  const [vendorNameSearch, setVendorNameSearch] = useState('')

  useEffect(() => {
    if (!isInitFetchStockLocations) fetchStockLocations()
  }, [currentPageStockLocations, currentLimitStockLocations, statusStockLocations])

  useEffect(() => {
    if (!isInitFetchVendors) fetchVendors()
  }, [vendorDropdownPage, vendorNameSearch])

  useEffect(() => {
    if (!isOpenModalFormStockLocation) handleResetFormStockLocation()
  }, [isOpenModalFormStockLocation])

  const handleChangeStatusStockLocation = (status: StockLocationStatusEnum | string) => {
    setStatusStockLocations(status)
    setCurrentPageStockLocations(1)
  }

  const handleChangePageStockLocation = (page: number) => {
    setCurrentPageStockLocations(page)
  }

  const handleChangeLimitStockLocation = (limit: number) => {
    setCurrentLimitStockLocations(limit)
    setCurrentPageStockLocations(1)
  }

  const handleResetFormStockLocation = () => {
    setInitialFormStockLocation({
      name: '',
      ownerName: '',
      ownerPhoneNo: '',
      address: '',
      province: '',
      city: '',
      invoiceCode: '',
      latitude: 0,
      longitude: 0,
      openHour: '',
      closeHour: '',
      isShownMember: true,
      isShownWorker: true,
      status: 'ACTIVE',
      vendorId: '',
      referenceLocation: '',
      imageUrlItems: [],
    })
    setStockLocationId('')
    setIsInitFetchVendors(true)
    setVendorsDropdown([])
    setVendorDropdownPage(1)
    setVendorsDropdownPages(1)
    setVendorNameSearch('')
    setVendorInitialName('')
  }

  const handleChangeVendorNameDropdown = debounce((value: string) => {
    setIsSearchVendor(true)
    setVendorDropdownPage(1)
    setVendorNameSearch(value)
  }, 500)

  const handleOpenCreateFormStockLocation = async () => {
    try {
      setIsOpenModalFormStockLocation(true)
      setIsLoadingFormStockLocation(true)
      await Promise.all([fetchProvinces(), fetchVendors()])
      setStockLocationId('')
      setStateFormStockLocation(StateFormEnum.CREATE)
      setIsLoadingFormStockLocation(false)
      setIsInitFetchVendors(false)
    } catch (e) {
      setIsLoadingFormStockLocation(false)
    }
  }

  const handleOpenUpdateFormStockLocation = async (id: string) => {
    try {
      setIsOpenModalFormStockLocation(true)
      setIsLoadingFormStockLocation(true)
      await Promise.all([fetchProvinces(), fetchVendors(), fetchStockLocationDetail(id)])
      setStockLocationId(id)
      setStateFormStockLocation(StateFormEnum.UPDATE)
      setIsLoadingFormStockLocation(false)
      setIsInitFetchVendors(false)
    } catch (e) {
      const errorStock = e as AxiosError
      const responseStatus = errorStock.response?.status
      if (responseStatus === 404) {
        await toast('Data tidak ditemukan', 'danger')
      } else {
        await toast('Tidak dapat terhubung ke server, silakan mencoba kembali.', 'danger')
      }
      setIsLoadingFormStockLocation(false)
      setIsOpenModalFormStockLocation(false)
    }
  }

  const handleCancelFormStockLocation = () => {
    setIsOpenModalFormStockLocation(false)
    setIsLoadingFormStockLocation(false)
  }

  const handleSubmit = (
    values:
      | AdminsEcommerceInventoryStockLocationCreateRequest
      | AdminsEcommerceInventoryStockLocationUpdateRequest,
  ) => {
    if (stateFormStockLocation === StateFormEnum.CREATE) {
      createStockLocation(values as AdminsEcommerceInventoryStockLocationCreateRequest)
    } else {
      updateStockLocation(values as AdminsEcommerceInventoryStockLocationUpdateRequest)
    }
  }

  const setFormStockLocation = (
    data:
      | AdminsEcommerceInventoryStockLocationCreateRequest
      | AdminsEcommerceInventoryStockLocationUpdateRequest,
  ) => {
    return {
      name: data.name,
      ownerName: data.ownerName,
      ownerPhoneNo: data.ownerPhoneNo,
      address: data.address,
      province: data.province,
      city: data.city,
      invoiceCode: data.invoiceCode,
      latitude: data.latitude,
      longitude: data.longitude,
      openHour: data.openHour,
      closeHour: data.closeHour,
      isShownMember: data.isShownMember,
      isShownWorker: data.isShownWorker,
      status: data.status,
      ...(data.imageUrlItems?.length ? { imageUrlItems: data.imageUrlItems } : undefined),
      ...(data.referenceLocation ? { referenceLocation: data.referenceLocation } : undefined),
      ...(data.vendorId ? { vendorId: data.vendorId } : undefined),
    }
  }

  const createStockLocation = async (data: AdminsEcommerceInventoryStockLocationCreateRequest) => {
    try {
      setIsLoadingFormStockLocation(true)
      const formStockLocation = setFormStockLocation(data)
      await adminsApi.adminsEcommerceInventoryStockLocationCreate('', formStockLocation)
      await toast('Berhasil menambahkan lokasi stok', 'success')
      setIsLoadingFormStockLocation(false)
      setIsOpenModalFormStockLocation(false)
      await fetchStockLocations()
    } catch (err) {
      setIsOpenModalFormStockLocation(true)
      setIsLoadingFormStockLocation(false)
      const errorStock = err as AxiosError
      const responseStatus = errorStock.response?.status
      const expectedResponseStatus = [400, 500]
      if (responseStatus && expectedResponseStatus.includes(responseStatus)) {
        if (responseStatus === 400) {
          await toast(enumToastMessage.err_400, 'danger')
        } else if (responseStatus === 500) {
          await toast(enumToastMessage.err_500, 'danger')
        }
      } else {
        await toast('Tidak dapat terhubung ke server, silakan mencoba kembali.', 'danger')
      }
    }
  }

  const updateStockLocation = async (data: AdminsEcommerceInventoryStockLocationUpdateRequest) => {
    try {
      setIsLoadingFormStockLocation(true)
      const formStockLocation = setFormStockLocation(data)
      await adminsApi.adminsEcommerceInventoryStockLocationUpdate(
        stockLocationId,
        '',
        formStockLocation as AdminsEcommerceInventoryStockLocationUpdateRequest,
      )
      await toast('Berhasil update lokasi stok', 'success')
      setIsLoadingFormStockLocation(false)
      setIsOpenModalFormStockLocation(false)
      await fetchStockLocations()
    } catch (err) {
      setIsOpenModalFormStockLocation(true)
      setIsLoadingFormStockLocation(false)
      const errorStock = err as AxiosError
      const responseStatus = errorStock.response?.status
      const expectedResponseStatus = [400]
      if (responseStatus && expectedResponseStatus.includes(responseStatus)) {
        if (responseStatus >= 400 && responseStatus < 500) {
          await toast(enumToastMessage.err_400, 'danger')
        }
      } else {
        await toast(enumToastMessage.err_500, 'danger')
      }
    }
  }

  const fetchStockLocations = async (): Promise<void> => {
    try {
      setIsLoadingStockLocations(true)
      setIsFailedStockLocations(false)
      const { data } = await adminsApi.adminsEcommerceInventoryStockLocationList(
        '',
        currentPageStockLocations,
        currentLimitStockLocations,
        statusStockLocations === '' ? undefined : (statusStockLocations as StockLocationStatusEnum),
      )
      setStockLocations(data.items)
      setTotalRowsStockLocations(data.pagination.total)
      setIsLoadingStockLocations(false)
      setIsInitFetchStockLocations(false)
    } catch (e) {
      setIsFailedStockLocations(true)
      setIsLoadingStockLocations(false)
      setIsInitFetchStockLocations(false)
    }
  }

  const fetchProvinces = async () => {
    const { data } = await locationApi.locationGetProvince('')
    setProvincesDropdown(data.items)
  }

  const fetchVendors = async () => {
    setIsLoadingFetchVendors(true)
    const { data } = await adminsApi.adminsFinanceGetVendorList(
      vendorDropdownPage,
      10,
      VendorStatusEnum.Active,
      vendorNameSearch ? vendorNameSearch : undefined,
    )
    if (isSearchVendor) {
      setVendorsDropdown(data.items)
    } else {
      setVendorsDropdown([...vendorsDropdown, ...data.items])
    }
    setIsLoadingFetchVendors(false)
    setIsSearchVendor(false)
    setVendorsDropdownPages(data.pagination?.pages as number)
  }

  const handlePopUpScrollVendors = (event: React.UIEvent<HTMLDivElement>) => {
    const target = event.target as HTMLDivElement
    if (
      Math.ceil(target.scrollTop) + Math.ceil(target.offsetHeight) >= target.scrollHeight &&
      vendorsDropdownPages > vendorDropdownPage
    ) {
      setVendorDropdownPage(vendorDropdownPage + 1)
    }
  }

  const fetchStockLocationDetail = async (id: string) => {
    const { data } = await adminsApi.adminsEcommerceInventoryStockLocationDetail(id, '')
    setInitialFormStockLocation({
      name: data.name,
      ownerName: data.ownerName,
      ownerPhoneNo: data.ownerPhoneNo,
      address: data.address,
      province: data.province,
      city: data.city,
      invoiceCode: data.invoiceCode,
      latitude: data.latitude,
      longitude: data.longitude,
      openHour: data.openHour,
      closeHour: data.closeHour,
      isShownMember: data.isShownMember,
      isShownWorker: data.isShownWorker,
      status: data.status,
      vendorId: data.vendorId,
      referenceLocation: data.referenceLocation,
      imageUrlItems: data.imageUrlItems,
    })
    setVendorInitialName(data.vendorName)
  }

  return {
    toastContainer,
    isLoadingStockLocations,
    isLoadingFetchVendors,
    isFailedStockLocations,
    statusStockLocations,
    stockLocations,
    totalRowsStockLocations,
    currentPageStockLocations,
    currentLimitStockLocations,
    isOpenModalFormStockLocation,
    isLoadingFormStockLocation,
    initialFormStockLocation,
    vendorInitialName,
    provincesDropdown,
    vendorsDropdown,
    stateFormStockLocation,
    handleChangeVendorNameDropdown,
    handlePopUpScrollVendors,
    handleSubmit,
    handleChangeStatusStockLocation,
    handleChangePageStockLocation,
    handleChangeLimitStockLocation,
    handleOpenCreateFormStockLocation,
    handleOpenUpdateFormStockLocation,
    handleCancelFormStockLocation,
    fetchStockLocations,
    fetchVendors,
  }
}
